import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, AlertTriangle, CheckCircle, XCircle,
  Clock, Ban, Eye, ExternalLink, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * FOUNDER SPONSOR ADMIN PANEL — LOCKED DIRECTIVE
 * 
 * Founder-only access (no delegated admin role).
 * Manual review of all sponsor submissions.
 * 
 * Checklist-based approval (not creative decisions):
 * - Category alignment
 * - Content accuracy
 * - No external embeds
 * - No redirects
 * - No misleading claims
 * - No child exposure risk
 * - No political manipulation
 * 
 * 3-stage enforcement for violations.
 */

export default function FounderSponsorAdmin() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allSponsors, setAllSponsors] = useState([]);
  const [violations, setViolations] = useState([]);
  // Monthly Exposure (Renders) tab
  const [renderData, setRenderData] = useState(null);
  const [creditPool, setCreditPool] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [checklist, setChecklist] = useState({
    category_aligned: false,
    content_accurate: false,
    no_external_embeds: false,
    no_redirects: false,
    no_misleading_claims: false,
    no_child_exposure_risk: false,
    no_political_manipulation: false,
  });
  const [rejectionReason, setRejectionReason] = useState('');

  // Check founder access
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (user.role !== 'founder' && !user.is_admin) {
      toast.error('Founder access required');
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const [pendingRes, sponsorsRes, violationsRes] = await Promise.all([
        axios.get(`${API}/api/founder/sponsors/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/founder/sponsors/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/founder/sponsors/violations`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      setPendingReviews(pendingRes.data.pending_reviews || []);
      setAllSponsors(sponsorsRes.data.sponsors || []);
      setViolations(violationsRes.data.violations || []);
      
      // Fetch render data and credit pool
      const [renderRes, poolRes] = await Promise.all([
        axios.get(`${API}/api/founder/sponsors/renders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/founder/credits/pool`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setRenderData(renderRes.data);
      setCreditPool(poolRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Founder access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const seedHiddenMindset = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/sponsors/seed-hiddenmindset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed sponsor');
    }
  };

  const handleApprove = async () => {
    if (!selectedPlacement) return;
    
    // All checklist items must be checked
    const allChecked = Object.values(checklist).every(v => v);
    if (!allChecked) {
      toast.error('All checklist items must pass for approval');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/sponsors/approve`, {
        sponsor_id: selectedPlacement.placement_id,
        approved: true,
        ...checklist,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Placement approved');
      setSelectedPlacement(null);
      resetChecklist();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!selectedPlacement) return;
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/sponsors/approve`, {
        sponsor_id: selectedPlacement.placement_id,
        approved: false,
        rejection_reason: rejectionReason,
        ...checklist,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Placement rejected');
      setSelectedPlacement(null);
      setRejectionReason('');
      resetChecklist();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleViolation = async (sponsorId, violationType) => {
    const description = prompt(`Describe the ${violationType} violation:`);
    if (!description) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/founder/sponsors/violation`, {
        sponsor_id: sponsorId,
        violation_type: violationType,
        description,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Violation recorded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record violation');
    }
  };

  const resetChecklist = () => {
    setChecklist({
      category_aligned: false,
      content_accurate: false,
      no_external_embeds: false,
      no_redirects: false,
      no_misleading_claims: false,
      no_child_exposure_risk: false,
      no_political_manipulation: false,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'rejected': return '#ef4444';
      case 'suspended': return '#f97316';
      case 'banned': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getViolationStageLabel = (stage) => {
    switch (stage) {
      case 0: return 'None';
      case 1: return 'Stage 1 - Warning';
      case 2: return 'Stage 2 - Suspended';
      case 3: return 'Stage 3 - Banned';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/founder')}
                className="p-2 rounded-lg text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                <h1 className="text-lg font-bold text-white">Sponsor Admin</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate('/founder/policy-log')}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Policy Log
              </Button>
              <Button
                onClick={fetchData}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            onClick={seedHiddenMindset}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Seed HiddenMindset Sponsor
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'pending', label: 'Pending Review', count: pendingReviews.length },
            { id: 'sponsors', label: 'All Sponsors', count: allSponsors.length },
            { id: 'violations', label: 'Violations', count: violations.length },
            { id: 'renders', label: 'Monthly Exposure', count: null },
            { id: 'credits', label: 'Credit Pool', count: null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-zinc-700">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Pending Reviews Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingReviews.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No pending reviews
              </div>
            ) : (
              pendingReviews.map(placement => (
                <div
                  key={placement.placement_id}
                  className="bg-zinc-800 rounded-xl p-4 border border-zinc-700"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-white">{placement.title}</h3>
                      <p className="text-sm text-zinc-400">{placement.description}</p>
                    </div>
                    <span className="px-2 py-1 text-xs rounded bg-amber-600/20 text-amber-400">
                      Pending
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs text-zinc-500 mb-3">
                    <span>Type: {placement.placement_type}</span>
                    {placement.category_id && <span>• Category: {placement.category_id}</span>}
                    {placement.sponsor_info && (
                      <span>• Sponsor: {placement.sponsor_info.business_name}</span>
                    )}
                  </div>

                  {placement.image_url && (
                    <img
                      src={placement.image_url}
                      alt={placement.title}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}

                  {placement.destination_url && (
                    <a
                      href={placement.destination_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:underline flex items-center gap-1 mb-3"
                    >
                      {placement.destination_url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  <Button
                    onClick={() => {
                      setSelectedPlacement(placement);
                      resetChecklist();
                    }}
                    className="w-full bg-zinc-700 hover:bg-zinc-600 text-white"
                  >
                    Review
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* All Sponsors Tab */}
        {activeTab === 'sponsors' && (
          <div className="space-y-4">
            {allSponsors.map(sponsor => (
              <div
                key={sponsor.sponsor_id}
                className="bg-zinc-800 rounded-xl p-4 border border-zinc-700"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-white">{sponsor.business_name}</h3>
                    <p className="text-sm text-zinc-400">{sponsor.owner_email}</p>
                  </div>
                  <span
                    className="px-2 py-1 text-xs rounded"
                    style={{ 
                      backgroundColor: getStatusColor(sponsor.status) + '20',
                      color: getStatusColor(sponsor.status)
                    }}
                  >
                    {sponsor.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mb-3">
                  <span>Active Placements: {sponsor.active_placements || 0}</span>
                  <span>Violation Stage: {getViolationStageLabel(sponsor.violation_stage)}</span>
                  {sponsor.is_system_sponsor && (
                    <span className="text-amber-400">System Sponsor</span>
                  )}
                </div>

                {sponsor.status !== 'banned' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleViolation(sponsor.sponsor_id, 'content_policy')}
                      size="sm"
                      variant="outline"
                      className="text-amber-400 border-amber-400/30"
                    >
                      Report Violation
                    </Button>
                    <Button
                      onClick={() => handleViolation(sponsor.sponsor_id, 'child_safety')}
                      size="sm"
                      variant="outline"
                      className="text-red-400 border-red-400/30"
                    >
                      Child Safety Issue
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Violations Tab */}
        {activeTab === 'violations' && (
          <div className="space-y-4">
            {violations.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No violations recorded
              </div>
            ) : (
              violations.map(violation => (
                <div
                  key={violation.violation_id}
                  className="bg-zinc-800 rounded-xl p-4 border border-zinc-700"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs font-medium px-2 py-1 rounded bg-red-600/20 text-red-400">
                        {violation.violation_type}
                      </span>
                      <p className="mt-2 text-white">{violation.description}</p>
                    </div>
                    <span className="text-xs text-zinc-500">
                      Stage {violation.stage_applied}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">
                    <span>{violation.action_taken}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(violation.reported_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Monthly Exposure (Renders) Tab */}
        {activeTab === 'renders' && (
          <div className="space-y-6" data-testid="renders-tab">
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-lg font-bold text-white mb-2">Monthly Exposure Report</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Aggregate render counts only. No per-user, no per-session, no real-time tracking.
              </p>
              
              {renderData ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-white">{renderData.total_renders}</p>
                      <p className="text-xs text-zinc-500">Total Renders</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-amber-400">{renderData.sponsor_count}</p>
                      <p className="text-xs text-zinc-500">Active Sponsors</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-lg font-bold text-zinc-300">{renderData.month}</p>
                      <p className="text-xs text-zinc-500">Report Month</p>
                    </div>
                  </div>

                  {/* By Sponsor */}
                  <h4 className="text-sm font-medium text-zinc-400 mb-3">By Sponsor</h4>
                  {renderData.by_sponsor.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No render data for this month</p>
                  ) : (
                    <div className="space-y-2">
                      {renderData.by_sponsor.map(sponsor => (
                        <div 
                          key={sponsor.sponsor_id}
                          className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg"
                        >
                          <span className="text-white">{sponsor.sponsor_name}</span>
                          <span className="text-amber-400 font-mono">{sponsor.total_renders}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* By Category */}
                  {Object.keys(renderData.by_category || {}).length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-zinc-400 mt-6 mb-3">By Category</h4>
                      <div className="space-y-2">
                        {Object.entries(renderData.by_category).map(([category, count]) => (
                          <div 
                            key={category}
                            className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg"
                          >
                            <span className="text-zinc-300">{category}</span>
                            <span className="text-zinc-400 font-mono">{count}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-zinc-500">No render data available</p>
              )}
            </div>
          </div>
        )}

        {/* Credit Pool Tab */}
        {activeTab === 'credits' && (
          <div className="space-y-6" data-testid="credits-tab">
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-lg font-bold text-white mb-2">Credit Pool Status</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Monthly sponsor credits with 70/30 split. 70% → Participation pool, 30% → LikeKind retained.
              </p>
              
              {creditPool ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-white">{creditPool.total_sponsor_credits}</p>
                      <p className="text-xs text-zinc-500">Total Credits</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-400">{creditPool.participation_pool}</p>
                      <p className="text-xs text-zinc-500">Participation Pool (70%)</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-amber-400">{creditPool.likekind_retained}</p>
                      <p className="text-xs text-zinc-500">LikeKind Retained (30%)</p>
                    </div>
                    <div className="bg-zinc-900 rounded-lg p-4 text-center">
                      <p className="text-lg font-bold text-zinc-300">{creditPool.month}</p>
                      <p className="text-xs text-zinc-500">Pool Month</p>
                    </div>
                  </div>

                  <div className="p-4 bg-zinc-900 rounded-lg">
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">Pool Split</h4>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-green-600/30 rounded-lg p-2 text-center">
                        <span className="text-green-400 font-medium">70%</span>
                        <p className="text-xs text-zinc-400">Participation</p>
                      </div>
                      <div className="flex-1 bg-amber-600/30 rounded-lg p-2 text-center">
                        <span className="text-amber-400 font-medium">30%</span>
                        <p className="text-xs text-zinc-400">LikeKind</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-zinc-500">No credit pool data available</p>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Review Modal */}
      {selectedPlacement && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div className="w-full max-w-lg bg-zinc-800 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Review Placement</h2>
            
            <div className="mb-4 p-3 rounded-lg bg-zinc-900">
              <h3 className="font-bold text-white">{selectedPlacement.title}</h3>
              <p className="text-sm text-zinc-400">{selectedPlacement.description}</p>
              {selectedPlacement.destination_url && (
                <a
                  href={selectedPlacement.destination_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:underline flex items-center gap-1 mt-2"
                >
                  View Link <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Checklist */}
            <div className="space-y-3 mb-6">
              <h4 className="text-sm font-medium text-zinc-400">Approval Checklist (all required)</h4>
              {[
                { key: 'category_aligned', label: 'Category alignment verified' },
                { key: 'content_accurate', label: 'Content is accurate' },
                { key: 'no_external_embeds', label: 'No external embeds' },
                { key: 'no_redirects', label: 'No suspicious redirects' },
                { key: 'no_misleading_claims', label: 'No misleading claims' },
                { key: 'no_child_exposure_risk', label: 'No child exposure risk' },
                { key: 'no_political_manipulation', label: 'No political manipulation' },
              ].map(item => (
                <label
                  key={item.key}
                  className="flex items-center gap-3 p-2 rounded-lg bg-zinc-900 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checklist[item.key]}
                    onChange={(e) => setChecklist({
                      ...checklist,
                      [item.key]: e.target.checked
                    })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-white">{item.label}</span>
                </label>
              ))}
            </div>

            {/* Rejection Reason */}
            <div className="mb-6">
              <label className="text-sm font-medium text-zinc-400 block mb-2">
                Rejection Reason (if rejecting)
              </label>
              <Input
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this placement is being rejected"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setSelectedPlacement(null);
                  resetChecklist();
                  setRejectionReason('');
                }}
                variant="outline"
                className="flex-1 border-zinc-600 text-zinc-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!Object.values(checklist).every(v => v)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
